import { useContext, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import AuthContext from '../../context/authContext';
import EditableList from '../EditableList/EditableList';
import useUser from '../../hooks/useUser';
import useAuthApi from '../../hooks/useAuthApi';
import { toast } from 'react-toastify';
import { Button, Tooltip, Box, FormControlLabel, Switch, CircularProgress } from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import SecurityIcon from '@mui/icons-material/Security';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import VpnKeyOffIcon from '@mui/icons-material/VpnKeyOff';
import useAirKey from '../../hooks/useAirKey';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

function UserManagement() {
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();
  const { deactivateUser } = useUser();
  const { enableWebauthnBreakglass } = useAuthApi();
  const { activateForUser, checkMappingsForUser } = useAirKey();
  const [showInactive, setShowInactive] = useState(false);
  const [activatingAirKey, setActivatingAirKey] = useState(null);
  const canManageCcEmployees = authCtx.checkRight('user:special:manageCcEmployees');
  const canUpdateUsers = authCtx.checkRight('user:update');
  const isCcEmployeeScopedOnly = canManageCcEmployees && !canUpdateUsers;
  const canReadAirKey = authCtx.checkRight('airkey:read');
  const canActivateAirKey = authCtx.checkRight('airkey:activate') || authCtx.checkRight('airkey:activate:own');

  const mapWebauthnStatusFields = useMemo(() => {
    return item => {
      const credentials = Array.isArray(item?.webauthn?.credentials)
        ? item.webauthn.credentials
        : [];
      const hasCredential = credentials.length > 0;
      const breakGlassUntil = item?.webauthn?.breakGlassUntil
        ? new Date(item.webauthn.breakGlassUntil)
        : null;
      const gracePeriodEndsAt = item?.webauthn?.gracePeriodEndsAt
        ? new Date(item.webauthn.gracePeriodEndsAt)
        : null;
      const isBreakGlassActive = breakGlassUntil && breakGlassUntil.getTime() > Date.now();

      let webauthnStatusLabel = 'Nicht registriert';
      if (hasCredential) {
        webauthnStatusLabel = 'Registriert';
      } else if (isBreakGlassActive) {
        webauthnStatusLabel = 'Breakglass aktiv';
      } else if (gracePeriodEndsAt && gracePeriodEndsAt.getTime() > Date.now()) {
        webauthnStatusLabel = 'Grace aktiv';
      }

      const daysRemaining =
        gracePeriodEndsAt && gracePeriodEndsAt.getTime() > Date.now()
          ? Math.ceil((gracePeriodEndsAt.getTime() - Date.now()) / (24 * 60 * 60 * 1000))
          : 0;

      return {
        ...item,
        webauthnStatusLabel,
        webauthnGraceDaysRemaining: daysRemaining,
      };
    };
  }, []);

  // Prüfe ob der Benutzer die notwendigen Rechte hat
  if (!authCtx.checkRight('user:read') && !canManageCcEmployees) {
    return <Navigate to='/welcome' replace />;
  }

  const userSchema = {
    isActive: {
      label: 'Aktiv',
      api: { itemField: 'isActive' },
      type: 'boolean',
      required: false,
      readOnly: true,
      hideInCreate: true,
      minWidth: '80px',
    },
    firstName: {
      label: 'Vorname',
      api: {
        itemField: 'firstName',
      },
      placeholder: 'Vornamen eingeben',
      type: 'text',
      required: true,
      minWidth: '120px',
    },
    lastName: {
      label: 'Nachname',
      api: {
        itemField: 'lastName',
      },
      placeholder: 'Nachnamen eingeben',
      type: 'text',
      required: true,
      minWidth: '120px',
    },
    username: {
      label: 'Benutzername',
      api: {
        itemField: 'username',
      },
      placeholder: 'Benutzername eingeben',
      type: 'text',
      required: true,
      hideInCreate: true,
      minWidth: '100px',
    },
    email: {
      label: 'Email',
      api: {
        itemField: 'email',
      },
      placeholder: 'Email eingeben',
      type: 'email',
      required: true,
      minWidth: '180px',
    },
    phone: {
      label: 'Telefon',
      api: {
        itemField: 'phone',
      },
      placeholder: 'Telefon eingeben',
      type: 'phone',
      required: false,
      minWidth: '120px',
    },
    birthDate: {
      label: 'Geburtsdatum',
      api: {
        itemField: 'birthDate',
      },
      placeholder: 'Geburtsdatum eingeben',
      type: 'date',
      required: false,
      minWidth: '120px',
    },
    password: {
      label: 'Password',
      excludeFromAdd: true,
      api: {
        itemField: 'password',
      },
      type: 'password',
      required: true,
    },
    roles: {
      label: 'Rollen',
      type: 'multi-select',
      readOnly: isCcEmployeeScopedOnly,
      api: {
        fieldName: 'roles',
        itemField: 'roles',
        idName: 'roleId',
        resource: 'role',
        labelField: 'name',
        valueField: '_id',
      },
      placeholder: 'Rollen auswählen',
    },
    assignedBranches: {
      label: 'Zugehörige Filialen',
      type: 'multi-select',
      readOnly: isCcEmployeeScopedOnly,
      api: {
        fieldName: 'branches',
        itemField: 'assignedBranches',
        idName: 'branchId',
        resource: 'branch',
        labelField: 'longName',
        valueField: '_id',
      },
      placeholder: 'Filialen auswählen',
    },
    assignedGroups: {
      label: 'Zugehörige Gruppen',
      type: 'multi-select',
      readOnly: isCcEmployeeScopedOnly,
      api: {
        fieldName: 'group',
        itemField: 'assignedGroups',
        idName: 'groupId',
        resource: 'group',
        labelField: 'name',
        valueField: '_id',
      },
      placeholder: 'Gruppen auswählen',
    },
    assignedComdeskAgent: {
      label: 'Comdesk Agent',
      type: 'object',
      excludeFromAdd: true,
      api: {
        itemField: 'assignedComdeskAgent',
        skipSpecial: true,
      },
      schema: {
        id: {
          label: 'Agent ID',
          type: 'text',
          required: true,
        },
        description: {
          label: 'Description',
          type: 'text',
          required: true,
        },
        skillGroups: {
          label: 'Skill Groups',
          type: 'array',
          placeholder: 'Enter skill group IDs (comma separated)',
          displayValue: value => {
            if (!value || !Array.isArray(value)) {
              return '—';
            }
            return value.join(', ');
          },
          transformValue: value => {
            return value
              .toString()
              .split(',')
              .map(id => id.trim());
          },
          listDisplayValue: value => {
            if (!value || !Array.isArray(value)) {
              return '—';
            }
            if (value.length === 0) {
              return 'Keine';
            }
            if (value.length <= 3) {
              return value.join(', ');
            }
            return `${value.slice(0, 3).join(', ')} (+${value.length - 3} weitere)`;
          },
        },
      },
      displayValue: value => value?.id || '—',
    },
    cardId: {
      label: 'Kartennummer',
      type: 'text',
      required: false,
      minWidth: '120px',
      readOnly: true,
      excludeFromAdd: true,
    },
    cardStatus: {
      label: 'Kartenzustand',
      type: 'select',
      required: false,
      minWidth: '120px',
      options: [
        { value: 'open', label: 'warte auf Bestätigung' },
        { value: 'confirmed', label: 'warte auf Druck' },
        { value: 'printed', label: 'Gedruckt' },
        { value: 'replace', label: 'Ersetzen' },
        { value: 'replaceWithoutLetter', label: 'Ersetzen ohne Brief' },
      ],
    },
    employeeId: {
      label: 'Mitarbeiternummer',
      type: 'text',
      required: false,
      minWidth: '120px',
      readOnly: true,
      excludeFromAdd: true,
    },
    plannedDeactivationAt: {
      label: 'Geplante Deaktivierung',
      type: 'date',
      required: false,
      minWidth: '180px',
      excludeFromAdd: true,
    },
    webauthnStatusLabel: {
      label: 'WebAuthn Status',
      api: { itemField: 'webauthnStatusLabel' },
      type: 'text',
      required: false,
      readOnly: true,
      excludeFromAdd: true,
      minWidth: '160px',
    },
    webauthnGraceDaysRemaining: {
      label: 'Grace (Tage)',
      api: { itemField: 'webauthnGraceDaysRemaining' },
      type: 'number',
      required: false,
      readOnly: true,
      excludeFromAdd: true,
      minWidth: '120px',
    },
    // Personal data for employment
    personalData: {
      label: 'Personendaten',
      type: 'object',
      excludeFromAdd: false,
      api: {
        itemField: 'personalData',
        skipSpecial: true,
      },
      schema: {
        streetAndNumber: {
          label: 'Straße und Hausnummer',
          type: 'text',
          required: false,
          placeholder: 'Musterstraße 123',
        },
        zip: {
          label: 'PLZ',
          type: 'text',
          required: false,
          placeholder: '12345',
        },
        city: {
          label: 'Ort',
          type: 'text',
          required: false,
          placeholder: 'Musterstadt',
        },
        gender: {
          label: 'Geschlecht',
          type: 'select',
          required: false,
          options: [
            { value: 'M', label: 'Männlich' },
            { value: 'W', label: 'Weiblich' },
            { value: 'D', label: 'Divers' },
            { value: '', label: 'keine Angabe' },
          ],
        },
        birthPlace: {
          label: 'Geburtsort',
          type: 'text',
          required: false,
          placeholder: 'Geburtsort eingeben',
        },
        nationality: {
          label: 'Nationalität',
          type: 'text',
          required: false,
          placeholder: 'Nationalität eingeben',
        },
      },
    },
    // Fiscal data for employment
    fiscalData: {
      label: 'Fiskaldaten',
      type: 'object',
      excludeFromAdd: false,
      api: {
        itemField: 'fiscalData',
        skipSpecial: true,
      },
      schema: {
        iban: {
          label: 'IBAN',
          type: 'text',
          required: false,
          placeholder: 'DE89 3704 0044 0532 0130 00',
        },
        bic: {
          label: 'BIC',
          type: 'text',
          required: false,
          placeholder: 'COBADEFFXXX',
        },
        differentAccountHolder: {
          label: 'Abweichender Kontoinhaber',
          type: 'text',
          required: false,
          placeholder: 'Name des Kontoinhabers',
        },
      },
    },
    // Employment data
    employmentData: {
      label: 'Anstellungsdaten',
      type: 'object',
      excludeFromAdd: false,
      api: {
        itemField: 'employmentData',
        skipSpecial: true,
      },
      schema: {
        jobField: {
          label: 'Tätigkeitsfeld',
          type: 'text',
          required: false,
          placeholder: 'Tätigkeitsfeld eingeben',
        },
        employmentType: {
          label: 'Anstellungsform',
          type: 'text',
          required: false,
          placeholder: 'Anstellungsform eingeben',
        },
        department: {
          label: 'Abteilung',
          type: 'text',
          required: false,
          placeholder: 'Abteilung eingeben',
        },
      },
    },
  };

  return (
    <Box sx={viewRootTextPrimarySx}>
      <Box sx={{ mb: 1 }}>
        <FormControlLabel
          control={
            <Switch
              size='small'
              checked={showInactive}
              onChange={e => setShowInactive(e.target.checked)}
            />
          }
          label='Inaktive anzeigen'
        />
      </Box>
      <EditableList
        schema={userSchema}
        title='Neuen Mitarbeiter hinzufügen'
        resourceType='user'
        headerSearchPlaceholder='Mitarbeiter durchsuchen'
        parentIsEditable={canUpdateUsers || canManageCcEmployees}
        options={{
          titleKey: 'username',
          transformItems: mapWebauthnStatusFields,
          disableCreate: isCcEmployeeScopedOnly,
          disableDelete: isCcEmployeeScopedOnly,
        }}
        visibleColumnKeys={[
          'firstName',
          'lastName',
          'username',
          'email',
          'roles',
          'assignedBranches',
          'assignedGroups',
          'employeeId',
          'plannedDeactivationAt',
          'webauthnStatusLabel',
          'webauthnGraceDaysRemaining',
        ]}
        listParams={{ includeInactive: showInactive }}
        renderRowActions={item => {
          const canDeactivate = authCtx.checkRight('user:special:deactivate');
          const canBreakglass = authCtx.checkRight('auth:webauthn:breakglass');
          if (!canDeactivate && !canBreakglass && !canReadAirKey && !canActivateAirKey) return null;
          const disabled = item?.isActive === false;
          const isActivatingAK = activatingAirKey === item._id;
          return (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {canReadAirKey && item?.airKeyPersonId && (
                <Tooltip title='AirKey-Daten anzeigen'>
                  <Button
                    size='small'
                    variant='outlined'
                    startIcon={<VpnKeyIcon />}
                    onClick={e => {
                      e.stopPropagation();
                      navigate(`/airkey?userId=${item._id}`);
                    }}
                  >
                    AirKey
                  </Button>
                </Tooltip>
              )}
              {canActivateAirKey && item?.isActive && (!item?.airKeyPersonId || item?.airKeyHasActiveMedia === false) && (
                <Tooltip title={item?.airKeyPersonId ? 'AirKey-Zugang reaktivieren' : 'AirKey-Zugang aktivieren'}>
                  <Button
                    size='small'
                    variant='outlined'
                    color='success'
                    startIcon={isActivatingAK ? <CircularProgress size={16} /> : <VpnKeyOffIcon />}
                    disabled={isActivatingAK || disabled}
                    onClick={async e => {
                      e.stopPropagation();
                      setActivatingAirKey(item._id);
                      try {
                        const result = await activateForUser(item._id);
                        if (result.action === 'no-mappings') {
                          toast.info('Kein AirKey-Mapping für diesen User vorhanden');
                        } else if (result.success) {
                          toast.success(`AirKey aktiviert (${result.action})`);
                          window.dispatchEvent(
                            new CustomEvent('editableListRefresh', { detail: { resource: 'user' } })
                          );
                        } else {
                          toast.warn(`AirKey teilweise aktiviert: ${result.errors?.[0] || 'Fehler'}`);
                        }
                      } catch (err) {
                        toast.error('Fehler beim Aktivieren von AirKey');
                      } finally {
                        setActivatingAirKey(null);
                      }
                    }}
                  >
                    {item?.airKeyPersonId ? 'AirKey re-aktiv.' : 'AirKey aktiv.'}
                  </Button>
                </Tooltip>
              )}
              {canBreakglass && (
                <Button
                  size='small'
                  variant='outlined'
                  color='warning'
                  startIcon={<SecurityIcon />}
                  disabled={disabled}
                  onClick={async e => {
                    e.stopPropagation();
                    try {
                      await enableWebauthnBreakglass(item._id);
                      toast.success('MFA Bypass für 24 Stunden aktiviert');
                      window.dispatchEvent(
                        new CustomEvent('editableListRefresh', { detail: { resource: 'user' } })
                      );
                    } catch (error) {
                      toast.error('Fehler beim Aktivieren von MFA Bypass');
                      throw error;
                    }
                  }}
                >
                  MFA Bypass 24h
                </Button>
              )}
              {canDeactivate && (
                <Button
                  size='small'
                  variant='outlined'
                  color='error'
                  startIcon={<BlockIcon />}
                  disabled={disabled}
                  onClick={async e => {
                    e.stopPropagation();
                    try {
                      await deactivateUser({ userId: item._id, immediate: true });
                      // trigger list refresh
                      window.dispatchEvent(
                        new CustomEvent('editableListRefresh', { detail: { resource: 'user' } })
                      );
                    } catch (_) {}
                  }}
                >
                  Sperren
                </Button>
              )}
            </Box>
          );
        }}
      />
    </Box>
  );
}

export default UserManagement;
